import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, ShieldCheck, FileText, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { PdfPreviewViewer } from '../ui/PdfPreviewViewer';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/* MLS-REVIEW-PACKAGE-003 — mirrors ReviewTaxCertModal 1:1: report-only preview (same PdfPreviewViewer: page nav,
   scroll, download, open in new tab), mandatory acknowledgement, then POST /components/mls/review + complete.
   Supporting documents are NOT part of this preview — they are handled by the final delivery package. */
const ReviewMlsReportModal = ({ open, onOpenChange, orderId, onConfirm }) => {
  const [pdfData, setPdfData] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const urlRef = useRef('');

  const fetchPdf = useCallback(async () => {
    setLoading(true); setPdfData(null); setLoadError(null); setAcknowledged(false);
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = ''; }
    setPdfUrl('');
    try {
      const res = await fetch(`${API}/api/orders/${orderId}/components/mls/report/preview-pdf`, { headers: auth() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const d = err.detail;
        setLoadError(typeof d === 'string' ? d : d?.message || 'Could not generate the MLS Search Report preview.');
        return;
      }
      const buf = await res.arrayBuffer();
      setPdfData(new Uint8Array(buf));
      const url = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }));
      urlRef.current = url; setPdfUrl(url);
    } catch {
      setLoadError('Failed to load the MLS Search Report preview.');
    } finally { setLoading(false); }
  }, [orderId]);

  const confirm = useCallback(async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/orders/${orderId}/components/mls/review`, { method: 'POST', headers: auth() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : err.detail?.message || 'Could not record the review');
        return;
      }
      onOpenChange(false);
      await onConfirm?.();
    } finally { setSubmitting(false); }
  }, [orderId, onConfirm, onOpenChange]);

  useEffect(() => { if (open) fetchPdf(); }, [open]); // eslint-disable-line
  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);
  const viewerEl = useMemo(() => ((pdfData || pdfUrl)
    ? <PdfPreviewViewer pdfData={pdfData} fallbackUrl={pdfUrl} className="min-h-[500px]" /> : null), [pdfData, pdfUrl]);

  if (!open) return null;
  const canConfirm = acknowledged && !submitting && !loading && !loadError && !!pdfData;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="review-mls-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 text-white" style={{ background: '#004aad' }}>
          <h3 className="text-[15px] font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Review MLS Search Report</h3>
          <button onClick={() => onOpenChange(false)} data-testid="review-mls-close-btn" className="p-1 rounded hover:bg-white/15"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="text-[12.5px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3" data-testid="review-mls-info-note">
            Review the generated <span className="font-semibold">MLS Search Report</span> preview below before confirming completion.
          </div>
          {loadError && (
            <div className="flex items-start gap-2.5 bg-rose-50 border border-rose-300 rounded-lg px-4 py-3 text-[12.5px] text-rose-900" data-testid="review-mls-load-error">
              <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
              <span>{loadError} Completion stays blocked until the report can be generated.</span>
            </div>
          )}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
              <FileText className="h-4 w-4 text-slate-500" />
              <span className="text-[13px] font-semibold text-slate-700">MLS Search Report</span>
              <span className="text-[11px] text-slate-400 ml-auto">Generated from MLS Fulfillment data</span>
            </div>
            {loading ? (
              <div className="h-64 flex items-center justify-center text-slate-400 text-sm" data-testid="review-mls-pdf-loading"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Generating MLS Search Report preview…</div>
            ) : viewerEl || (
              <div className="p-8 text-center" data-testid="review-mls-preview-unavailable"><AlertCircle className="h-8 w-8 text-amber-400 mx-auto mb-2" /><p className="text-sm font-medium text-slate-600">Preview unavailable.</p></div>
            )}
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer select-none bg-amber-50/60 border border-amber-200 rounded-lg px-4 py-3" data-testid="review-mls-acknowledge-label">
            <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} disabled={loading || !!loadError || !pdfData}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" data-testid="review-mls-acknowledge-checkbox" />
            <span className="text-[12.5px] text-slate-700 font-medium">I have reviewed and confirm the MLS Search Report is accurate.</span>
          </label>
        </div>
        <div className="flex items-center justify-end gap-2.5 px-5 py-3.5 border-t border-slate-200 bg-slate-50">
          <button onClick={() => onOpenChange(false)} data-testid="review-mls-cancel-btn" className="px-4 py-2 text-[12.5px] font-semibold border border-slate-300 text-slate-600 hover:bg-slate-100 rounded-md transition-colors">Cancel</button>
          <button onClick={confirm} disabled={!canConfirm} data-testid="review-mls-confirm-btn"
            className="px-4 py-2 text-[12.5px] font-semibold text-white rounded-md transition-colors disabled:opacity-40 inline-flex items-center gap-1.5" style={{ background: '#004aad' }}>
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
            {submitting ? 'Completing…' : 'Confirm & Complete'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ReviewMlsReportModal);
